import {
  Alert,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import type { BiomeshPlatformResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  getListPlatformsBiomeshPlatformListGetQueryKey,
  useCreatePlatformBiomeshPlatformPost,
  useListOperationsBiomeshOperationListGet,
  useUpdatePlatformBiomeshPlatformPlatformIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { Modal } from "../Shared/Modal"
import {
  extractApiError,
  isNodeType,
  latLngToMgrs,
  mgrsToLatLng,
  nodeTypeOptions,
  parseCentroid,
  toCentroid,
  validateMgrs,
} from "./copUtils"
import { MultiAutocomplete } from "./MultiAutocomplete"

type CoordMode = "latlng" | "mgrs"

interface NodeFormDialogProps {
  open: boolean
  onClose: () => void
  node?: BiomeshPlatformResponse
  onSaved?: (nodeId: string) => void
  /** Pre-fill coordinates when creating from a map click */
  initialCoords?: { lat: number; lng: number }
  /** When true, open with "No location" checked by default */
  defaultUnmapped?: boolean
}

export const NodeFormDialog: FC<NodeFormDialogProps> = ({
  open,
  onClose,
  node,
  onSaved,
  initialCoords,
  defaultUnmapped,
}) => {
  const isEdit = !!node
  const queryClient = useQueryClient()
  const existingCoords = node ? parseCentroid(node.centroid) : initialCoords

  const [name, setName] = useState(node?.name ?? "")
  const [nodeType, setNodeType] = useState(node?.platform_type ?? "")
  const [operationIds, setOperationIds] = useState<string[]>(
    () =>
      node?.operations
        ?.map(o => o.operation_id)
        .filter((id): id is string => !!id) ?? []
  )
  const [coordMode, setCoordMode] = useState<CoordMode>("mgrs")
  const [latitude, setLatitude] = useState(
    existingCoords ? String(existingCoords.lat) : ""
  )
  const [longitude, setLongitude] = useState(
    existingCoords ? String(existingCoords.lng) : ""
  )
  const [mgrsValue, setMgrsValue] = useState(
    existingCoords ? latLngToMgrs(existingCoords.lat, existingCoords.lng) : ""
  )
  const [noLocation, setNoLocation] = useState(
    defaultUnmapped ?? (isEdit && !existingCoords)
  )
  const [formError, setFormError] = useState("")

  const { data: operations } = useListOperationsBiomeshOperationListGet()

  const { mutateAsync: createNode, isPending: createPending } =
    useCreatePlatformBiomeshPlatformPost()
  const { mutateAsync: updateNode, isPending: updatePending } =
    useUpdatePlatformBiomeshPlatformPlatformIdPut()

  const isPending = createPending || updatePending

  const handleSave = useCallback(async () => {
    setFormError("")

    if (!name.trim()) {
      setFormError("Name is required")
      return
    }
    if (!nodeType) {
      setFormError("Node type is required")
      return
    }

    let centroid: string | undefined
    if (noLocation) {
      centroid = undefined
    } else {
      try {
        if (coordMode === "mgrs") {
          if (!mgrsValue.trim()) {
            setFormError("MGRS coordinate is required")
            return
          }
          const mgrsError = validateMgrs(mgrsValue.trim())
          if (mgrsError) {
            setFormError(mgrsError)
            return
          }
          const { lat, lng } = mgrsToLatLng(mgrsValue.trim())
          centroid = toCentroid(lat, lng)
        } else {
          const lat = Number(latitude)
          const lng = Number(longitude)
          if (
            !latitude.trim() ||
            !longitude.trim() ||
            isNaN(lat) ||
            isNaN(lng)
          ) {
            setFormError("Valid latitude and longitude are required")
            return
          }
          centroid = toCentroid(lat, lng)
        }
      } catch {
        setFormError("Invalid coordinate value")
        return
      }
    }

    if (!isNodeType(nodeType)) {
      setFormError("Invalid node type")
      return
    }

    const payload = {
      name: name.trim(),
      platform_type: nodeType,
      centroid,
      operation_ids: operationIds.length > 0 ? operationIds : undefined,
    }

    try {
      if (node) {
        await updateNode({
          platformId: node.platform_id,
          data: payload,
        })
        enqueueSnackbar("Node updated", { variant: "success" })
        onSaved?.(node.platform_id)
      } else {
        const result = await createNode({
          data: payload,
        })
        enqueueSnackbar("Node created", { variant: "success" })
        onSaved?.(result.platform_id)
      }
      await queryClient.invalidateQueries({
        queryKey: getListPlatformsBiomeshPlatformListGetQueryKey(),
      })
      onClose()
    } catch (err) {
      setFormError(extractApiError(err, "Failed to save node"))
    }
  }, [
    name,
    nodeType,
    noLocation,
    coordMode,
    mgrsValue,
    latitude,
    longitude,
    operationIds,
    node,
    createNode,
    updateNode,
    queryClient,
    onClose,
    onSaved,
  ])

  const operationOptions =
    operations?.map(o => ({ id: o.operation_id, label: o.name })) ?? []

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        onConfirm={() => void handleSave()}
        title={isEdit ? "Edit Node/Facility" : "New Node/Facility"}
        confirmLabel="Save"
        disableBtns={isPending}
      >
        <Stack spacing={1.5}>
          <TextField
            label="Name"
            required
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
          />

          <TextField
            label="Type"
            select
            required
            fullWidth
            value={nodeType}
            onChange={e => setNodeType(e.target.value)}
          >
            {nodeTypeOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.name}
              </MenuItem>
            ))}
          </TextField>

          <MultiAutocomplete
            label="Operations"
            options={operationOptions}
            selectedIds={operationIds}
            onChange={setOperationIds}
          />

          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
            }}
          >
            Location
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={noLocation}
                onChange={e => setNoLocation(e.target.checked)}
              />
            }
            label="No location (unmapped)"
          />

          {!noLocation && (
            <>
              <RadioGroup
                row
                value={coordMode}
                onChange={e => setCoordMode(e.target.value as CoordMode)}
              >
                <FormControlLabel
                  value="latlng"
                  control={<Radio />}
                  label="Lat / Lng"
                />
                <FormControlLabel
                  value="mgrs"
                  control={<Radio />}
                  label="MGRS"
                />
              </RadioGroup>

              {coordMode === "latlng" ? (
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    label="Latitude"
                    required
                    fullWidth
                    value={latitude}
                    onChange={e => setLatitude(e.target.value)}
                  />
                  <TextField
                    label="Longitude"
                    required
                    fullWidth
                    value={longitude}
                    onChange={e => setLongitude(e.target.value)}
                  />
                </Stack>
              ) : (
                <TextField
                  label="MGRS"
                  required
                  fullWidth
                  placeholder="e.g. 33UUP0500"
                  value={mgrsValue}
                  onChange={e => setMgrsValue(e.target.value)}
                />
              )}
            </>
          )}
          {formError && <Alert severity="error">{formError}</Alert>}
        </Stack>
      </Modal>
    </>
  )
}
